/**
 * lib/ninetailed/env.ts
 *
 * Single source of truth for the Ninetailed SDK's public configuration. These are
 * read from `NEXT_PUBLIC_*` environment variables (not tenant/Contentful config):
 * they are global to the deployment, and being `NEXT_PUBLIC_`-prefixed they are
 * inlined at build time and safe to ship to the browser (only the public Client
 * ID and environment slug — never a server token).
 *
 * IMPORTANT: `process.env.NEXT_PUBLIC_*` must be referenced as a full static
 * member expression for Next.js to inline it at build time. Do NOT rewrite these
 * as dynamic lookups (e.g. `process.env[name]`) — that breaks the inlining and
 * the values become `undefined` in the client bundle.
 */

/** Public Ninetailed Client ID. Empty string when unset -> SDK stays un-mounted. */
export const NINETAILED_CLIENT_ID =
  process.env.NEXT_PUBLIC_NINETAILED_CLIENT_ID ?? '';

/** Ninetailed environment slug (e.g. `main`). Undefined lets the SDK default it. */
export const NINETAILED_ENVIRONMENT =
  process.env.NEXT_PUBLIC_NINETAILED_ENVIRONMENT || undefined;

/**
 * True only when a Client ID is configured for this deployment. Consumers gate
 * the SDK mount on this (mirrors the previous per-tenant check).
 */
export const isNinetailedConfigured = Boolean(NINETAILED_CLIENT_ID);

/**
 * Force the Ninetailed SDK into preview mode in the local development environment
 * (`NEXT_PUBLIC_ENV=development`), so authors always get the live editor/preview
 * connection locally without toggling Contentful's generic preview flag. In every
 * other environment (stage/preprod/prod) the SDK's preview mode follows the
 * generic preview state (`useGetPreviewMode()`) instead. See the provider.
 */
export const NINETAILED_FORCE_PREVIEW =
  process.env.NEXT_PUBLIC_ENV === 'development';
